import React, { createContext, useEffect, useMemo, useState } from 'react';
import { setNotify } from '../utils/methods/notification';
import { Alert, Snackbar } from '@mui/material';

const NotificationContext = createContext({});

export const NotificationProvider = ({ children }) => {
  const [currentNotification, setCurrentNotification] = useState(null);

  const formatNotificationMessage = useMemo(() => {
    const MAX_LEN = 800;

    const safeStringify = (value) => {
      try {
        const seen = new WeakSet();
        return JSON.stringify(
          value,
          (k, v) => {
            if (typeof v === 'object' && v !== null) {
              if (seen.has(v)) return '[Circular]';
              seen.add(v);
            }
            return v;
          },
          2
        );
      } catch (_) {
        try {
          return String(value);
        } catch (__) {
          return 'Something went wrong';
        }
      }
    };

    const pickFirstString = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      for (const [key, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.trim()) return `${key}: ${val}`;
        if (Array.isArray(val) && typeof val[0] === 'string') return `${key}: ${val[0]}`;
      }
      return null;
    };

    return (message) => {
      if (message == null) return '';
      if (React.isValidElement(message)) return message;
      if (typeof message === 'string') return message;
      if (typeof message === 'number' || typeof message === 'boolean') return String(message);

      // Error / Axios-like error shapes
      const extracted =
        message?.response?.data?.message ??
        message?.data?.message ??
        message?.message ??
        message?.error ??
        null;

      if (typeof extracted === 'string' && extracted.trim()) return extracted;

      const responseData = message?.response?.data;
      const firstFieldError =
        pickFirstString(responseData?.errors) ||
        pickFirstString(responseData) ||
        pickFirstString(message);
      if (firstFieldError) return firstFieldError;

      const rendered = safeStringify(responseData ?? message);
      return rendered.length > MAX_LEN ? `${rendered.slice(0, MAX_LEN)}…` : rendered;
    };
  }, []);

  const handleNotification = (message, options = {}) => {
    const notification = {
      id: Date.now(),
      message: formatNotificationMessage(message),
      severity: options?.severity || 'default',
      duration: options?.duration || 3000,
      position: options?.position || { vertical: 'top', horizontal: 'right' }
    };

    setCurrentNotification(notification);
  };

  useEffect(() => {
    // Register the notification handler globally
    setNotify(handleNotification);
  }, []);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setCurrentNotification(null);
  };

  return (
    <NotificationContext.Provider value={{}}>
      {children}
      {currentNotification && (
        <Snackbar
          open={!!currentNotification}
          autoHideDuration={currentNotification?.duration}
          onClose={handleClose}
          anchorOrigin={currentNotification?.position}
          disableWindowBlurListener // ✅ stops observer from reacting to blur/focus
          >
          <Alert
            onClose={handleClose}
            severity={currentNotification?.severity}
            variant="filled"
            sx={{ width: "100%", color: "white", fontSize: 16 }}
          >
            {currentNotification?.message}
          </Alert>
        </Snackbar>
      )}
    </NotificationContext.Provider>
  );
};
